import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { DollarSign, Users, Wallet, TrendingUp, Receipt, BarChart3, LineChart, PieChart, IndianRupee, Clock, ArrowRight, ShieldAlert } from 'lucide-react';
import { PayPayoutModal } from '../components/PayPayoutModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pnlData, setPnlData] = useState(null);
  const [pending, setPending] = useState({ count: 0, items: [], totals: { INR: 0, USDT: 0 } });
  const [dq, setDq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roiView, setRoiView] = useState('cumulative');
  const [currency, setCurrency] = useState('USDT');
  const [payTarget, setPayTarget] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchPnL();
    fetchPending();
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (u.is_admin) {
      axiosInstance.get('/admin/data-quality').then((r) => setDq(r.data)).catch(() => setDq(null));
    }
  }, []);

  const fetchPending = async () => {
    try {
      const res = await axiosInstance.get('/payouts/pending?limit=10');
      setPending(res.data || { count: 0, items: [], totals: { INR: 0, USDT: 0 } });
    } catch (error) {
      console.error('Error fetching pending payouts:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPnL = async () => {
    try {
      const response = await axiosInstance.get(`/company/pnl?year=${new Date().getFullYear()}`);
      setPnlData(response.data);
    } catch (error) {
      console.error('Error fetching P&L:', error);
    }
  };

  // Get current conversion rate - prioritize latest_conversion_rate from API
  const getConversionRate = () => {
    return stats?.latest_conversion_rate || stats?.active_cycle?.inr_to_usdt_rate || 94;
  };

  // Convert USDT to INR
  const toINR = (usdtValue) => {
    if (usdtValue === null || usdtValue === undefined) return 0;
    return usdtValue * getConversionRate();
  };

  // Format value based on selected currency
  const formatValue = (usdtValue, showSign = false) => {
    if (usdtValue === null || usdtValue === undefined) return '0';
    
    const value = currency === 'INR' ? toINR(usdtValue) : usdtValue;
    const prefix = showSign && value > 0 ? '+' : '';
    
    if (currency === 'INR') {
      // Format in Lakhs/Crores for large numbers
      if (Math.abs(value) >= 10000000) {
        return `${prefix}${(value / 10000000).toFixed(2)} Cr`;
      } else if (Math.abs(value) >= 100000) {
        return `${prefix}${(value / 100000).toFixed(2)} L`;
      }
      return `${prefix}${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
    }
    return `${prefix}${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Display-only abbreviation for the Total AUM figure (no calculation change).
  // USDT: >= 1M -> "X.XXM", >= 1K -> "XXXK". INR: keep Cr/L convention.
  const formatAUM = (usdtValue) => {
    if (usdtValue === null || usdtValue === undefined) return '0';
    const value = currency === 'INR' ? toINR(usdtValue) : usdtValue;
    if (currency === 'INR') {
      if (Math.abs(value) >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
      if (Math.abs(value) >= 100000) return `${(value / 100000).toFixed(2)} L`;
      return value.toLocaleString('en-IN', { maximumFractionDigits: 0 });
    }
    if (Math.abs(value) >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
    if (Math.abs(value) >= 1000) return `${Math.round(value / 1000)}K`;
    return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  // Exact, unabbreviated value for the hover tooltip.
  const formatAUMExact = (usdtValue) => {
    if (usdtValue === null || usdtValue === undefined) return '0';
    const value = currency === 'INR' ? toINR(usdtValue) : usdtValue;
    return `${currencySymbol}${value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  };

  // Currency symbol
  const currencySymbol = currency === 'INR' ? '₹' : '';
  const currencyLabel = currency === 'INR' ? 'INR' : 'USDT';

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8" data-testid="dashboard">
      <div className="cb-page-header">
        <div className="flex justify-between items-end" style={{ gap: 'var(--cb-space-4)' }}>
          <div>
            <p className="cb-eyebrow">Overview</p>
            <h1 className="cb-h1">Dashboard</h1>
          </div>

          {/* Currency Toggle */}
          <div className="cb-segment" data-testid="currency-toggle">
            <button
              onClick={() => setCurrency('USDT')}
              data-testid="currency-usdt-btn"
              className={`cb-btn-secondary ${currency === 'USDT' ? 'cb-btn-secondary--active' : ''}`}
              style={{ border: 'none' }}
            >
              <DollarSign className="w-4 h-4" />
              USDT
            </button>
            <button
              onClick={() => setCurrency('INR')}
              data-testid="currency-inr-btn"
              className={`cb-btn-secondary ${currency === 'INR' ? 'cb-btn-secondary--active' : ''}`}
              style={{ border: 'none' }}
            >
              <IndianRupee className="w-4 h-4" />
              INR
            </button>
          </div>
        </div>
        <hr className="cb-divider" />
      </div>

      {/* Conversion Rate Notice */}
      {currency === 'INR' && (
        <div
          className="cb-muted"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--cb-space-2)',
            fontSize: '0.8125rem',
            padding: 'var(--cb-space-2) var(--cb-space-4)',
            background: 'var(--cb-bone)',
            border: '1px solid var(--cb-line)',
            borderRadius: 'var(--cb-radius)',
            marginBottom: 'var(--cb-space-4)',
          }}
        >
          <IndianRupee className="w-4 h-4" />
          <span className="cb-num">Conversion Rate: 1 USDT = ₹{getConversionRate().toLocaleString()}</span>
          {stats?.active_cycle && <span style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px' }}>(from active cycle)</span>}
        </div>
      )}

      {/* Data Quality Banner — admin only, when issues detected */}
      {dq && dq.total_issues > 0 && (
        <button
          onClick={() => navigate('/data-quality')}
          className="cb-notice"
          style={{ marginBottom: 'var(--cb-space-6)' }}
          data-testid="dq-dashboard-banner"
        >
          <ShieldAlert className="w-6 h-6 cb-notice-icon" />
          <div style={{ flex: 1 }}>
            <p className="cb-notice-title">
              {dq.total_issues} data integrity {dq.total_issues === 1 ? 'issue' : 'issues'} detected
            </p>
            <p className="cb-notice-sub">
              {dq.issue_groups.slice(0, 3).map((g) => `${g.title} (${g.count})`).join(' · ')}
              {dq.issue_groups.length > 3 && ` · +${dq.issue_groups.length - 3} more`}
            </p>
          </div>
          <span className="cb-notice-action">Review →</span>
        </button>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <button onClick={() => navigate('/clients')} className="cb-card cb-card--clickable" data-testid="stat-total-aum">
          <div className="flex items-center justify-between">
            <div>
              <p className="cb-card-label">Total AUM ({currencyLabel})</p>
              <p className="cb-card-value cb-num" title={formatAUMExact(stats?.total_aum_usdt)}>{currencySymbol}{formatAUM(stats?.total_aum_usdt)}</p>
            </div>
            {currency === 'INR' ? (
              <IndianRupee className="w-8 h-8 cb-card-icon" />
            ) : (
              <DollarSign className="w-8 h-8 cb-card-icon" />
            )}
          </div>
        </button>

        <button onClick={() => navigate('/clients')} className="cb-card cb-card--clickable" data-testid="stat-total-clients">
          <div className="flex items-center justify-between">
            <div>
              <p className="cb-card-label">Total Clients</p>
              <p className="cb-card-value cb-num">{stats?.total_clients || '0'}</p>
            </div>
            <Users className="w-8 h-8 cb-card-icon" />
          </div>
        </button>

        <button onClick={() => navigate('/company-pnl')} className="cb-card cb-card--clickable" data-testid="stat-company-balance">
          <div className="flex items-center justify-between">
            <div>
              <p className="cb-card-label">Company Balance ({currencyLabel})</p>
              <p className={`cb-card-value cb-num ${stats?.company_balance_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`}>
                {currencySymbol}{formatValue(stats?.company_balance_usdt)}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 cb-card-icon" />
          </div>
        </button>

        <button onClick={() => navigate('/expenses')} className="cb-card cb-card--clickable" data-testid="stat-total-expenses">
          <div className="flex items-center justify-between">
            <div>
              <p className="cb-card-label">Total Expenses ({currencyLabel})</p>
              <p className="cb-card-value cb-num cb-negative">{currencySymbol}{formatValue(stats?.total_expenses_usdt)}</p>
            </div>
            <Receipt className="w-8 h-8 cb-card-icon" />
          </div>
        </button>
      </div>

      {/* Pending Payouts Widget */}
      {pending.count > 0 && (
        <div className="cb-card mb-8" data-testid="pending-payouts-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center" style={{ gap: 'var(--cb-space-3)' }}>
              <Clock className="w-5 h-5" style={{ color: 'var(--cb-ink-soft)' }} />
              <div>
                <h3 className="cb-section-title">Pending Payouts</h3>
                <p className="cb-section-sub cb-num">
                  {pending.count} unpaid · ₹{(pending.totals.INR || 0).toLocaleString('en-IN')} INR
                  {pending.totals.USDT > 0 && <> · {pending.totals.USDT.toLocaleString('en-IN')} USDT</>}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/cycles')}
              className="cb-btn-secondary"
              data-testid="pending-view-all-link"
            >
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div>
            {pending.items.slice(0, 5).map((p) => (
              <div
                key={p.id}
                className="flex items-center justify-between"
                style={{
                  padding: 'var(--cb-space-3) 0',
                  borderTop: '1px solid var(--cb-line)',
                }}
                data-testid={`pending-row-${p.id}`}
              >
                <div className="flex items-center flex-1 min-w-0" style={{ gap: 'var(--cb-space-4)' }}>
                  <span
                    className="truncate"
                    style={{ color: 'var(--cb-ink)', fontWeight: 500, fontSize: '0.875rem' }}
                    data-testid={`pending-client-link-${p.id}`}
                  >
                    {p.client_code} · {p.client_name}
                  </span>
                  <span className="cb-muted hidden sm:inline" style={{ fontSize: '0.75rem' }}>{p.cycle_name}</span>
                  {p.age_days > 7 && (
                    <span className="cb-pill">{p.age_days}d old</span>
                  )}
                </div>
                <div className="flex items-center" style={{ gap: 'var(--cb-space-3)' }}>
                  <div className="text-right">
                    <div className="cb-num" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.125rem', color: 'var(--cb-ink)' }}>
                      {p.currency === 'INR' ? '₹' : ''}{p.amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })} {p.currency}
                    </div>
                    <div className="cb-muted cb-num" style={{ fontSize: '0.6875rem' }}>{p.profit_percent}%</div>
                  </div>
                  <button
                    onClick={() => setPayTarget(p)}
                    className="cb-btn-primary"
                    data-testid={`pending-pay-btn-${p.id}`}
                  >
                    Pay
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {payTarget && (
        <PayPayoutModal
          payout={payTarget}
          onClose={() => setPayTarget(null)}
          onPaid={() => { setPayTarget(null); fetchPending(); }}
        />
      )}

      {/* Cash Pool & Cycle Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <button onClick={() => navigate('/cash-pool')} className="cb-card cb-card--clickable" data-testid="cash-pool-summary">
          <div className="flex items-center justify-between mb-4">
            <h3 className="cb-section-title">Cash Pool</h3>
            <Wallet className="w-6 h-6 cb-card-icon" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-baseline">
              <span className="cb-muted" style={{ fontSize: '0.875rem' }}>Total Balance</span>
              <span className="cb-num" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.25rem' }}>₹{stats?.cash_pool_inr?.toLocaleString() || '0'}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="cb-muted" style={{ fontSize: '0.875rem' }}>Petty Cash</span>
              <span className="cb-num" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.25rem' }}>₹{stats?.petty_cash_inr?.toLocaleString() || '0'}</span>
            </div>
            <div className="flex justify-between items-baseline">
              <span className="cb-muted" style={{ fontSize: '0.875rem' }}>Available for Conversion</span>
              <span className="cb-num" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.25rem', color: 'var(--cb-ink)' }}>
                ₹{((stats?.cash_pool_inr || 0) - (stats?.petty_cash_inr || 0)).toLocaleString()}
              </span>
            </div>
            {currency === 'USDT' && (
              <div className="flex justify-between items-baseline" style={{ borderTop: '1px solid var(--cb-line)', paddingTop: 'var(--cb-space-2)', marginTop: 'var(--cb-space-2)' }}>
                <span className="cb-muted" style={{ fontSize: '0.8125rem' }}>USDT Equivalent</span>
                <span className="cb-num cb-muted" style={{ fontSize: '0.875rem' }}>
                  ~{((stats?.cash_pool_inr || 0) / getConversionRate()).toLocaleString('en-IN', { maximumFractionDigits: 2 })} USDT
                </span>
              </div>
            )}
          </div>
        </button>

        {stats?.active_cycle ? (
          <button onClick={() => navigate('/cycles')} className="cb-card cb-card--clickable" data-testid="active-cycle-info">
            <h3 className="cb-section-title mb-4">Active Cycle</h3>
            <div className="space-y-3" style={{ fontSize: '0.875rem' }}>
              <div>
                <span className="cb-muted">Name:</span>
                <span className="ml-2" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>{stats.active_cycle.name}</span>
              </div>
              <div>
                <span className="cb-muted">Type:</span>
                <span className="ml-2 capitalize" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>{stats.active_cycle.type?.replace('_', ' ') || 'N/A'}</span>
              </div>
              <div>
                <span className="cb-muted">Period:</span>
                <span className="ml-2 cb-num" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>
                  {new Date(stats.active_cycle.start_date).toLocaleDateString()} - {new Date(stats.active_cycle.end_date).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="cb-muted">Default Profit %:</span>
                <span className="ml-2 cb-num cb-positive" style={{ fontWeight: 500 }}>{stats.active_cycle.default_profit_percent}%</span>
              </div>
              <div>
                <span className="cb-muted">INR Rate:</span>
                <span className="ml-2 cb-num" style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>₹{stats.active_cycle.inr_to_usdt_rate}/USDT</span>
              </div>
            </div>
          </button>
        ) : (
          <button onClick={() => navigate('/cycles')} className="cb-card cb-card--clickable" data-testid="no-active-cycle">
            <h3 className="cb-section-title mb-4">Active Cycle</h3>
            <div className="text-center" style={{ padding: 'var(--cb-space-7) 0' }}>
              <p className="cb-muted">No active cycle</p>
              <span className="cb-pill" style={{ marginTop: 'var(--cb-space-2)', display: 'inline-block' }}>Create a new cycle to start</span>
            </div>
          </button>
        )}
      </div>

      {/* Company P&L Summary */}
      {pnlData && (
        <button onClick={() => navigate('/company-pnl')} className="cb-card cb-card--clickable mb-8" data-testid="company-pnl-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="cb-section-title">Company P&L (YTD {pnlData.year})</h3>
              <p className="cb-section-sub">{pnlData.summary.cycle_count} cycle(s) completed</p>
            </div>
            <PieChart className="w-6 h-6 cb-card-icon" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
              <p className="cb-card-label">Profit Made</p>
              <p className="cb-card-value cb-card-value--sm cb-num cb-positive">
                {currencySymbol}{formatValue(pnlData.summary.total_profit_made_usdt)}
              </p>
              <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>{currencyLabel}</p>
            </div>
            <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
              <p className="cb-card-label">Distributed</p>
              <p className="cb-card-value cb-card-value--sm cb-num">
                -{currencySymbol}{formatValue(pnlData.summary.total_distributed_usdt)}
              </p>
              <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>{currencyLabel}</p>
            </div>
            <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
              <p className="cb-card-label">Expenses</p>
              <p className="cb-card-value cb-card-value--sm cb-num cb-negative">
                -{currencySymbol}{formatValue(pnlData.summary.total_expenses_usdt)}
              </p>
              <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>{currencyLabel}</p>
            </div>
            <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
              <p className="cb-card-label">Gross Retention</p>
              <p className={`cb-card-value cb-card-value--sm cb-num ${pnlData.summary.gross_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`}>
                {currencySymbol}{formatValue(pnlData.summary.gross_retention_usdt)}
              </p>
              <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>{currencyLabel}</p>
            </div>
            <div className="cb-card" style={{ padding: 'var(--cb-space-4)', boxShadow: 'none' }}>
              <p className="cb-card-label">Net Retention</p>
              <p className={`cb-card-value cb-card-value--sm cb-num ${pnlData.summary.net_retention_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`}>
                {currencySymbol}{formatValue(pnlData.summary.net_retention_usdt)}
              </p>
              <p className="cb-muted" style={{ fontSize: '0.6875rem', textTransform: 'uppercase', letterSpacing: '1px', marginTop: 'var(--cb-space-1)' }}>
                {pnlData.summary.net_retention_usdt >= 0 ? 'Surplus' : 'Loss'}
              </p>
            </div>
          </div>

          {/* Cycle Breakdown */}
          {pnlData.cycles.length > 0 && (
            <div style={{ borderTop: '1px solid var(--cb-line)', paddingTop: 'var(--cb-space-4)' }}>
              <p className="cb-card-label" style={{ marginBottom: 'var(--cb-space-3)' }}>By Cycle</p>
              <table className="cb-table">
                <tbody>
                  {pnlData.cycles.slice(0, 5).map((c) => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td className="cb-num cb-muted">Made: {currencySymbol}{formatValue(c.profit_made_usdt)}</td>
                      <td className="cb-num cb-muted">Dist: {currencySymbol}{formatValue(c.distributed_usdt)}</td>
                      <td className={`cb-num ${c.status === 'surplus' ? 'cb-positive' : 'cb-negative'}`} style={{ fontWeight: 500 }}>
                        {c.status === 'surplus' ? '✓' : '⚠'} {currencySymbol}{formatValue(c.retention_usdt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pnlData.cycles.length > 5 && (
                <p className="cb-muted text-center" style={{ fontSize: '0.75rem', marginTop: 'var(--cb-space-2)' }}>
                  + {pnlData.cycles.length - 5} more cycles. <span style={{ color: 'var(--cb-ink)', fontWeight: 500 }}>View all →</span>
                </p>
              )}
            </div>
          )}
        </button>
      )}

      {/* Company ROI Trend Section */}
      <div className="cb-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="cb-section-title">Company ROI Trend ({new Date().getFullYear()})</h3>
            <p className="cb-section-sub">
              Year to Date: <span className="cb-positive cb-num" style={{ fontWeight: 500 }}>
                {currencySymbol}{formatValue(stats?.yearly_cumulative)} {currencyLabel}
              </span>
            </p>
          </div>
          <div className="cb-segment">
            <button
              onClick={() => setRoiView('cumulative')}
              data-testid="roi-cumulative-btn"
              className={`cb-btn-secondary ${roiView === 'cumulative' ? 'cb-btn-secondary--active' : ''}`}
              style={{ border: 'none' }}
            >
              <LineChart className="w-4 h-4" />
              Cumulative
            </button>
            <button
              onClick={() => setRoiView('monthly')}
              data-testid="roi-monthly-btn"
              className={`cb-btn-secondary ${roiView === 'monthly' ? 'cb-btn-secondary--active' : ''}`}
              style={{ border: 'none' }}
            >
              <BarChart3 className="w-4 h-4" />
              Monthly
            </button>
          </div>
        </div>

        {roiView === 'cumulative' ? (
          /* Cumulative View */
          <div data-testid="roi-cumulative-view">
            {stats?.monthly_roi && stats.monthly_roi.length > 0 ? (
              <div>
                {/* Visual Bar Chart */}
                <div className="mb-4">
                  {stats.monthly_roi.map((item, idx) => {
                    const maxCumulative = stats.yearly_cumulative || 1;
                    const percentage = (item.cumulative_usdt / maxCumulative) * 100;
                    return (
                      <div key={idx} className="mb-2">
                        <div className="flex justify-between mb-1" style={{ fontSize: '0.8125rem' }}>
                          <span style={{ fontWeight: 500, color: 'var(--cb-ink)' }}>{item.month}</span>
                          <span className="cb-positive cb-num" style={{ fontWeight: 500 }}>
                            {currencySymbol}{formatValue(item.cumulative_usdt)} {currencyLabel}
                          </span>
                        </div>
                        <div className="cb-bar">
                          <div className="cb-bar-fill" style={{ width: `${percentage}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Table */}
                <div style={{ marginTop: 'var(--cb-space-4)', paddingTop: 'var(--cb-space-4)', borderTop: '1px solid var(--cb-line)', overflowX: 'auto' }}>
                  <table className="cb-table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="cb-num">Monthly Profit</th>
                        <th className="cb-num">Cumulative</th>
                        <th className="cb-num">Cycles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.monthly_roi.map((item, idx) => (
                        <tr key={idx}>
                          <td style={{ fontWeight: 500 }}>{item.month}</td>
                          <td className="cb-num cb-positive">
                            +{currencySymbol}{formatValue(item.profit_usdt)}
                          </td>
                          <td className="cb-num" style={{ fontWeight: 500 }}>
                            {currencySymbol}{formatValue(item.cumulative_usdt)}
                          </td>
                          <td className="cb-num cb-muted">{item.cycle_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>
                <BarChart3 className="w-12 h-12 mx-auto mb-2" style={{ opacity: 0.3 }} />
                <p>No closed cycles this year yet</p>
              </div>
            )}
          </div>
        ) : (
          /* Monthly View */
          <div data-testid="roi-monthly-view">
            {stats?.monthly_roi && stats.monthly_roi.length > 0 ? (
              <div>
                <div className="grid grid-cols-1 gap-2">
                  {stats.monthly_roi.map((item, idx) => {
                    const maxProfit = Math.max(...stats.monthly_roi.map(m => m.profit_usdt), 1);
                    const percentage = (item.profit_usdt / maxProfit) * 100;
                    return (
                      <div key={idx} className="flex items-center" style={{ gap: 'var(--cb-space-3)', padding: 'var(--cb-space-2) 0', borderBottom: '1px solid var(--cb-line)' }}>
                        <div style={{ width: '5rem', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--cb-ink)' }}>{item.month}</div>
                        <div className="flex-1 cb-bar cb-bar--tall" style={{ position: 'relative' }}>
                          <div
                            className="cb-bar-fill"
                            style={{ width: `${Math.max(percentage, 10)}%` }}
                          >
                            {percentage > 30 && (
                              <span className="cb-num">
                                {currencySymbol}{formatValue(item.profit_usdt)}
                              </span>
                            )}
                          </div>
                          {percentage <= 30 && (
                            <span className="cb-num" style={{ position: 'absolute', right: 'var(--cb-space-2)', top: '50%', transform: 'translateY(-50%)', color: 'var(--cb-ink)', fontSize: '0.75rem', fontWeight: 500 }}>
                              {currencySymbol}{formatValue(item.profit_usdt)} {currencyLabel}
                            </span>
                          )}
                        </div>
                        <div style={{ width: '4rem', textAlign: 'right' }}>
                          <span className="cb-muted" style={{ fontSize: '0.6875rem' }}>{item.cycle_count} cycle{item.cycle_count !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Monthly Total */}
                <div className="flex justify-between items-baseline" style={{ marginTop: 'var(--cb-space-4)', paddingTop: 'var(--cb-space-4)', borderTop: '1px solid var(--cb-line)' }}>
                  <span style={{ fontWeight: 500, color: 'var(--cb-ink)' }}>Year Total ({new Date().getFullYear()})</span>
                  <span className="cb-num cb-positive" style={{ fontFamily: 'var(--cb-serif)', fontSize: '1.5rem' }}>
                    {currencySymbol}{formatValue(stats.yearly_cumulative)} {currencyLabel}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center cb-muted" style={{ padding: 'var(--cb-space-7) 0' }}>
                <BarChart3 className="w-12 h-12 mx-auto mb-2" style={{ opacity: 0.3 }} />
                <p>No closed cycles this year yet</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* All-Time Cycle History */}
      {stats?.company_roi && stats.company_roi.length > 0 && (
        <div className="cb-card mt-6">
          <h3 className="cb-section-title mb-4">All Closed Cycles (Last 12)</h3>
          <table className="cb-table">
            <tbody>
              {stats.company_roi.map((cycle, idx) => (
                <tr key={idx}>
                  <td>
                    <span style={{ fontWeight: 500, color: 'var(--cb-ink)' }}>{cycle.cycle_name}</span>
                    <span className="cb-muted ml-2" style={{ fontSize: '0.75rem' }}>
                      {cycle.date ? new Date(cycle.date).toLocaleDateString() : 'N/A'}
                    </span>
                  </td>
                  <td className={`cb-num ${cycle.profit_usdt >= 0 ? 'cb-positive' : 'cb-negative'}`} style={{ fontWeight: 500 }}>
                    {cycle.profit_usdt >= 0 ? '+' : ''}{currencySymbol}{formatValue(cycle.profit_usdt)} {currencyLabel}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
